import { useState, useContext } from "react";
import {
  Box,
  Stack,
  Button,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import { useFormik, Form, FormikProvider } from "formik";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
//Srcs
import "./login.scss";
import { AuthContext } from "../../Context/AuthContext";
import LoginImage from "../../Assets/Logo.png";
import { USER_LOGIN } from "../../Schema/User";
import ChangeLanguage from "../../Layout/ChangeLanguage";

export default function Login() {
  const navigate = useNavigate();
  const { setAlert, dispatch, t } = useContext(AuthContext);
  const [iconReload, setIconReload] = useState(false);

  // hide password hook
  const [show, setShow] = useState(false);
  const handleClick = () => setShow(!show);

  const [userLogin] = useMutation(USER_LOGIN, {
    onCompleted: ({ userLogin }) => {
      if (userLogin?.isSuccess === true) {
        dispatch({
          type: "LOGGED_IN_USER",
          payload: {
            token: userLogin?.token,
          },
        });

        // ========================= login success =========================
        setAlert(true, "success", userLogin?.message);
        window.localStorage.setItem("token", userLogin?.token);
        window.localStorage.setItem(
          "userLogin",
          JSON.stringify(userLogin?.user)
        );
        navigate("/dashboard");
        setIconReload(false);
      } else {
        setAlert(true, "error", userLogin?.message);
        setIconReload(false);
      }
    },
    onError: (err) => {
      setAlert(true, "error", err?.message);
      console.log("error::", err);
    },
  });

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .required("Required")
      .test("email-or-phone", function (value) {
        if (!value) return false;

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^[0-9]{8,15}$/; // adjust to your rules

        if (emailRegex.test(value)) {
          return true; // valid email
        }

        if (phoneRegex.test(value)) {
          return true; // valid phone
        }

        // if it looks like it has @ but invalid email
        if (value.includes("@")) {
          return this.createError({ message: "Invalid email format" });
        }

        // else assume it's phone input but invalid
        return this.createError({ message: "Invalid phone number" });
      }),

    password: Yup.string()
      .required("Required")
      .min(8, "Password must be at least 8 characters!"),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },

    validationSchema: LoginSchema,
    onSubmit: async (values) => {
    
      setIconReload(true);
      userLogin({
        variables: {
          ...values,
        },
      });
    },
  });

  const { errors, touched, handleSubmit, getFieldProps } = formik;

  return (
    <Stack
      direction="column"
      justifyContent="center"
      className="login-container"
    >
      <Stack direction="row" justifyContent="center" sx={{ width: "100%" }}>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack
              spacing={3.5}
              position=" relative"
              direction="column"
              className="box-container"
            >
              <Box sx={{ position: "absolute", top: 13, right: 14 }}>
                <ChangeLanguage size="26px" />
              </Box>

              <Stack direction="column" spacing={1}>
                <Stack direction="row" justifyContent="center">
                  <img style={{ width: "120px" }} alt="logo" src={LoginImage} />
                </Stack>
                <Typography
                  variant="body2"
                  align="center"
                  className="login-subtitle"
                >
                  {t("thead_title_login")}
                </Typography>
              </Stack>

              <Stack
                direction="column"
                justifyContent="center"
                width="100%"
                className="box-login"
                spacing={2}
              >
                <Stack direction="column">
                  <Typography className="label-login">
                    {t("thead_email_or_phone")}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    className="input-login"
                    placeholder="example@company.com"
                    {...getFieldProps("email")}
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </Stack>

                <Stack>
                  <Typography className="label-login">
                    {t("thead_password")}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="password"
                    type={show ? "text" : "password"}
                    {...getFieldProps("password")}
                    error={Boolean(touched.password && errors.password)}
                    helperText={touched.password && errors.password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start" onClick={handleClick}>
                          {show ? (
                            <VisibilityIcon className="viewpw-icon" />
                          ) : (
                            <VisibilityOffIcon className="viewpw-icon" />
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>

                {/* <Link to="/forgotpassword">
                  <Typography className="forgot-pwd">
                    Forgot password?
                  </Typography>
                </Link> */}
              </Stack>
              <Stack direction="column">
                <Button
                  className="btn-sign-in"
                  type="submit"
                  endIcon={
                    iconReload ? (
                      <CircularProgress sx={{ color: "#fff" }} size="20px" />
                    ) : null
                  }
                >
                  {t("thead_sign_in")}
                </Button>
              </Stack>
              <Typography className="footer-copy-right">
                Copyright © 2025 Go Global IT. All rights reserved.
              </Typography>
            </Stack>
          </Form>
        </FormikProvider>
      </Stack>
    </Stack>
  );
}
